/**
 * Contact Us section — public landing-page enquiry form.
 *
 * Submits to the unauthenticated `POST /api/v1/contact` endpoint (see
 * `app.api.v1.contact`), which emails the enquiry to
 * `settings.CONTACT_RECIPIENT_EMAIL` with `Reply-To` set to the visitor's
 * own address. Validation mirrors the backend schema
 * (`app.schemas.contact.ContactRequest`) so visitors see field errors
 * immediately, without waiting on a round trip — the backend re-validates
 * everything regardless, since this form has no auth and must not trust
 * the client.
 */

import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import contactService, { type ContactFormValues } from "@/services/contactService";
import { extractErrorMessage } from "@/utils/errors";

const ENQUIRY_TYPES = [
  "General Enquiry",
  "Sales",
  "Product Support",
  "Broker Integration",
  "Partnership",
  "Other",
];

export function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormValues>({ defaultValues: { subject: "" } });

  const onSubmit = async (values: ContactFormValues) => {
    setServerError(null);
    setIsSubmitting(true);
    try {
      await contactService.submitEnquiry(values);
      setSubmitted(true);
      reset();
    } catch (error) {
      setServerError(extractErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ py: { xs: 10, md: 14 } }} id="contact">
      <Container maxWidth="md">
        <Stack alignItems="center" textAlign="center" spacing={2} mb={6}>
          <Chip label="Contact Us" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
          <Typography variant="h4" fontWeight={800}>
            Get in touch with our team
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520 }}>
            Have a question about TekStar Invest, broker integrations, or pricing? Send us a
            message and our team will get back to you shortly.
          </Typography>
        </Stack>

        <Paper variant="outlined" sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
          {submitted ? (
            <Alert severity="success" onClose={() => setSubmitted(false)}>
              Thank you for contacting TekInvest. Our team will get back to you shortly.
            </Alert>
          ) : (
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <Stack spacing={2.5}>
                {serverError && <Alert severity="error">{serverError}</Alert>}

                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Full Name"
                      required
                      fullWidth
                      autoComplete="name"
                      error={Boolean(errors.full_name)}
                      helperText={errors.full_name?.message}
                      {...register("full_name", {
                        required: "Full name is required",
                        maxLength: { value: 200, message: "Must be 200 characters or fewer" },
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email Address"
                      type="email"
                      required
                      fullWidth
                      autoComplete="email"
                      error={Boolean(errors.email)}
                      helperText={errors.email?.message}
                      {...register("email", {
                        required: "Email address is required",
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email address" },
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Mobile Number"
                      fullWidth
                      autoComplete="tel"
                      error={Boolean(errors.mobile)}
                      helperText={errors.mobile?.message}
                      {...register("mobile", {
                        minLength: { value: 7, message: "Enter a valid mobile number" },
                      })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Company Name"
                      fullWidth
                      autoComplete="organization"
                      error={Boolean(errors.company_name)}
                      helperText={errors.company_name?.message}
                      {...register("company_name", {
                        maxLength: { value: 200, message: "Must be 200 characters or fewer" },
                      })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      select
                      label="Subject / Enquiry Type"
                      fullWidth
                      defaultValue=""
                      error={Boolean(errors.subject)}
                      helperText={errors.subject?.message}
                      {...register("subject")}
                    >
                      <MenuItem value="">
                        <em>Select an enquiry type (optional)</em>
                      </MenuItem>
                      {ENQUIRY_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Message"
                      required
                      fullWidth
                      multiline
                      minRows={4}
                      error={Boolean(errors.message)}
                      helperText={errors.message?.message}
                      {...register("message", {
                        required: "Message is required",
                        minLength: { value: 10, message: "Message must be at least 10 characters" },
                        maxLength: { value: 5000, message: "Message must be 5000 characters or fewer" },
                      })}
                    />
                  </Grid>
                </Grid>

                <Box>
                  <Button type="submit" variant="contained" size="large" disabled={isSubmitting}>
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </Box>
              </Stack>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}

export default ContactSection;
